/**
 * FT-1546: A LOT'S CARD ART — the tall illustration on its tarot card (not the
 * round coin icon), shown UNTOUCHED: no inking, no Light/Shadow tint.
 *
 * The picture lives in the platform asset store (POST /api/assets/upload, the
 * endpoint Clocktower's custom audio already uses), which keeps the original
 * and makes WebP renditions of it. A Lot stores only two of their addresses:
 *
 *   { display, thumb }
 *     display  the full card (≤2048 px) — LotCard
 *     thumb    small places (≤256 px)   — a seat's tarot card
 *
 * The ORIGINAL is never drawn anywhere. The addresses ride the library row
 * (`cardArt`, the server's column) and the app role (`golemCardArt`, a
 * customRole template key, so the edition frame carries it to every player),
 * and the live overlay moves it like any other library field.
 *
 * Every reader goes through `cardArtOf`, which accepts only the platform's own
 * same-origin asset-file addresses (the server's rule, shared/protocols/
 * lots-card-art.ts) — so a crafted frame can never point a player's browser
 * at an outside URL.
 */

/** The asset-file route's content-addressed WebP path (the server's regex). */
const ART_URL_RE =
  /^\/api\/assets\/file\/[0-9a-f]{2}\/[0-9a-f]{64}(-[a-z0-9]+)?\.webp$/;

/** The platform upload's own cap (server/assets/routes.ts MAX_UPLOAD_BYTES). */
export const CARD_ART_MAX_BYTES = 10 * 1024 * 1024;

/** The login wording Clocktower's custom audio uses, minus its link aside. */
export const CARD_ART_LOGIN_MESSAGE =
  "Uploading needs a golem-studios.com login";

const okUrl = (v) => typeof v === "string" && ART_URL_RE.test(v);

/**
 * The card art `value` names, clean — `{ display, thumb }` — or null. Takes a
 * stored `{display, thumb}` object or anything carrying one (`golemCardArt` on
 * an app role, `cardArt` on a library row).
 */
export function cardArtOf(value) {
  if (!value || typeof value !== "object") return null;
  const art =
    "display" in value || "thumb" in value
      ? value
      : value.golemCardArt || value.cardArt;
  if (!art || typeof art !== "object") return null;
  const display = okUrl(art.display) ? art.display : "";
  const thumb = okUrl(art.thumb) ? art.thumb : "";
  if (!display && !thumb) return null;
  return { display: display || thumb, thumb: thumb || display };
}

/** The src for a FULL card — the display rendition. "" when none. */
export function cardArtDisplay(value) {
  const art = cardArtOf(value);
  return art ? art.display : "";
}

/** The src for a SMALL card (a seat) — the thumb rendition. "" when none. */
export function cardArtThumb(value) {
  const art = cardArtOf(value);
  return art ? art.thumb : "";
}

/**
 * Upload a picture as card art. Resolves `{ display, thumb }`; throws an Error
 * whose message is fit to show the author as it stands.
 *
 * The renditions: "display" for the card; an upload whose bytes were stored
 * before the display rendition existed (the store dedups by content) has none,
 * and falls back to its THUMB — a readable card, never the full-size original.
 */
export async function uploadCardArt(file) {
  if (!file) throw new Error("No file was picked");
  if (!/^image\//.test(file.type || "")) {
    throw new Error("That file is not an image");
  }
  if (file.size > CARD_ART_MAX_BYTES) {
    throw new Error("Too big — 10MB is the cap");
  }
  const body = new FormData();
  body.append("file", file, file.name || "card-art");
  body.append("kind", "lots_card_art");
  // unlisted: the address is handed out by the Lot itself, not by a gallery
  body.append("visibility", "unlisted");
  let res;
  try {
    res = await fetch("/api/assets/upload", {
      method: "POST",
      credentials: "same-origin",
      body,
    });
  } catch (e) {
    throw new Error("The upload could not reach the server");
  }
  if (res.status === 401) throw new Error(CARD_ART_LOGIN_MESSAGE);
  if (res.status === 413) throw new Error("Too big — 10MB is the cap");
  if (res.status === 415) throw new Error("That file is not an image");
  if (!res.ok) throw new Error(`The upload failed (${res.status})`);
  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    data = null;
  }
  const variants = (data && data.asset && data.asset.variants) || [];
  const urlOf = (name) => {
    const v = variants.find((x) => x && x.variant === name);
    return v && okUrl(v.url) ? v.url : "";
  };
  const thumb = urlOf("thumb");
  const display = urlOf("display") || thumb;
  if (!display || !thumb) {
    throw new Error("The upload came back without a picture to show");
  }
  return { display, thumb };
}
